import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getProposalConversations,
  createProposalConversation
} from '../api/proposal-conversations-api';
import {
  ProposalConversation,
  CreateProposalConversationRequest
} from '../models/ProposalConversation';
import { useSnackbarContext } from '../contexts/useSnackBarContext';
import { useTranslation } from 'react-i18next';

/**
 * Hook to get all conversations for a proposal (visible to current user)
 */
export const useProposalConversations = (proposalId: number, enabled = true) => {
  return useQuery<ProposalConversation[], Error>({
    queryKey: ['proposal-conversations', proposalId],
    queryFn: () => getProposalConversations(proposalId),
    enabled: enabled && !!proposalId,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true, 
  });
};

/**
 * Hook to create a new conversation message
 */
export const useCreateConversation = (proposalId: number) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbarContext();
  const { t } = useTranslation();

  return useMutation<
    ProposalConversation,
    Error,
    CreateProposalConversationRequest
  >({
    mutationFn: (request) => createProposalConversation(proposalId, request),
    onSuccess: () => {
      // Invalidate conversations cache to refresh the list
      queryClient.invalidateQueries({ queryKey: ['proposal-conversations', proposalId] });
      showSuccess(t('proposal.conversation.messageSent'));
    },
    onError: (error) => {
      showError(
        error.message || t('proposal.conversation.messageError')
      );
    }
  });
};
